import axios from 'axios';
import { makeObservable, observable, runInAction, when } from 'mobx';
import {
    IPartition,
    ITenantDetail,
    TenantManagementApi,
} from '@servicetitan/marketing-tenant-management-api';
import { DataResult, State } from '@servicetitan/data-query';
import { inject, injectable } from '@servicetitan/react-ioc';
import { AsyncDataSource, TableState } from '@servicetitan/table';
import { TABLE_PAGE_SIZE } from '../utils/ux';
import { noop } from '../../common/utils/noop';
import { ManageTenantStore } from './manage-tenant.store';
import { LoadStatus } from '../../common/enums/load-status';
import { getInitialTableState } from '../utils/get-initial-table-state';
import { PartitionsQueryParams } from '../utils/partitions-query-params-handler';

@injectable()
export class PartitionsStore {
    @observable tableState = new TableState<IPartition>({
        pageSize: TABLE_PAGE_SIZE,
    });
    @observable partitionsLoadStatus = LoadStatus.None;
    @observable deleteLoadStatus = LoadStatus.None;
    @observable searchFilter = '';

    @observable queryParams?: PartitionsQueryParams;

    tenantDetails!: ITenantDetail;
    abortController?: AbortController;

    constructor(
        @inject(ManageTenantStore) private readonly manageTenantStore: ManageTenantStore,
        @inject(TenantManagementApi) private readonly tenantManagementApi: TenantManagementApi,
    ) {
        makeObservable(this);
    }

    init = async (initialQueryParams: PartitionsQueryParams) => {
        const tenant = this.manageTenantStore.tenant;

        if (!tenant) {
            return;
        }

        runInAction(() => {
            this.tenantDetails = tenant;
            this.searchFilter = initialQueryParams.search ?? '';
        });

        await this.tableState.setDataSource(new AsyncDataSource({ get: this.fetchPartitions }), {
            initialState: {
                ...getInitialTableState(),
                skip: Math.floor(((initialQueryParams.page ?? 1) - 1) * TABLE_PAGE_SIZE),
                sort: initialQueryParams.sortField
                    ? [
                          {
                              dir: initialQueryParams.isAscending ? 'asc' : 'desc',
                              field: initialQueryParams.sortField,
                          },
                      ]
                    : [],
            },
        });
    };

    fetchPartitions = async (state: State): Promise<DataResult<IPartition>> => {
        const search = this.searchFilter;

        this.abortController?.abort();
        await when(() => this.partitionsLoadStatus !== LoadStatus.Loading);
        this.abortController = new AbortController();

        const prevLoadStatus = this.partitionsLoadStatus;

        try {
            runInAction(() => {
                this.partitionsLoadStatus = LoadStatus.Loading;
            });

            const skip = state.skip ?? 0;
            const page = Math.floor(skip / TABLE_PAGE_SIZE);

            let sortField: string | undefined;
            let isAscending: boolean | undefined;

            if (state.sort?.[0]) {
                sortField = state.sort[0].field;
                isAscending = state.sort[0].dir === 'asc';
            }

            this.queryParams = {
                page: page + 1,
                search,
                sortField,
                isAscending,
            };

            const {
                data: { partitions, count },
            } = await this.tenantManagementApi.all(
                this.tenantDetails.internalId,
                search,
                page,
                state.take,
                isAscending,
                sortField,
                this.abortController?.signal,
            );

            runInAction(() => {
                this.partitionsLoadStatus = LoadStatus.Ok;
            });

            return {
                data: partitions || [],
                total: count,
            };
        } catch (error) {
            if (axios.isCancel(error)) {
                runInAction(() => {
                    this.partitionsLoadStatus = prevLoadStatus;
                });

                return {
                    data: this.tableState.data,
                    total: this.tableState.totalCount,
                };
            }

            runInAction(() => {
                this.partitionsLoadStatus = LoadStatus.Error;
            });

            return {
                data: [],
                total: 0,
            };
        }
    };

    filterBySearch = async (searchFilter: string) => {
        runInAction(() => {
            this.searchFilter = searchFilter;
        });

        await this.tableState.fetchData({ newSkip: 0 });
    };

    deletePartition = async (internalPartitionId: IPartition['internalId']) => {
        runInAction(() => {
            this.deleteLoadStatus = LoadStatus.Loading;
        });

        try {
            await this.tenantManagementApi.partitionDELETE(
                this.tenantDetails.internalId,
                internalPartitionId,
            );

            this.tableState.fetchData().catch(noop);

            runInAction(() => {
                this.deleteLoadStatus = LoadStatus.Ok;
            });
        } catch {
            runInAction(() => {
                this.deleteLoadStatus = LoadStatus.Error;
            });
        }
    };
}
